import IconButton from '@mui/material/IconButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import React from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import DialogModal from 'components/generic/modals/dialog-modal'
import InputV2 from 'components/generic/form/InputV2'
import { useResetGsUserPasswordMutation } from 'services/gsUsers'
import { setSnackBar } from 'state/ui/index'
import { useAppDispatch } from 'utils/custom-hooks/useTypedReduxStore'
import { Typography } from '@mui/material_modern'

interface Props {
  open: boolean
  onClose: () => void
  userId: string
}

const schema = yup
  .object({
    newPassword: yup
      .string()
      .required('Password is required')
      .min(8, 'Must be at least 8 characters')
      .matches(/(?=.*\d)/, 'Must contain a number')
      .matches(/(?=.*[!@#$%^&*])/, 'Must contain a special character')
      .matches(/(?=.*[A-Z])/, 'Must contain an uppercase letter')
      .matches(/(?=.*[a-z])/, 'Must contain a lowercase letter'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('newPassword')], 'Passwords must match')
      .required('Please confirm your password'),
  })
  .required()

type FormData = yup.InferType<typeof schema>

const UserPasswordResetModal: React.FC<Props> = ({ open, onClose, userId }) => {
  const methods = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' },
  })
  const dispatch = useAppDispatch()

  const [resetPassword, { isLoading: isResetting }] = useResetGsUserPasswordMutation()

  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods

  const [showPassword, setShowPassword] = React.useState(false)

  const submit = handleSubmit(async data => {
    try {
      await resetPassword({ userId, resetUserPasswordRequestBody: { newPassword: data.newPassword } }).unwrap()
      reset()
      dispatch(
        setSnackBar({
          snackbarType: 'success',
          snackbarMessage: 'temporary password set',
        }),
      )
      onClose()
    } catch (error: unknown) {
      dispatch(
        setSnackBar({
          snackbarType: 'error',
          snackbarMessage: `There was an error resetting the password: ${(error as any)?.data?.error || 'Unknown error.'}`,
        }),
      )
    }
  })

  return (
    <FormProvider {...methods}>
      <DialogModal
        open={open}
        onClose={() => {
          reset()
          onClose()
        }}
        title="Reset Password"
        buttonTitle="Reset"
        secondaryButtonTitle={isResetting ? '' : 'Cancel'}
        onSave={submit}
        saveDisabled={!isValid || isResetting}
        dialogStyles={{ '& .MuiDialog-paper': { maxWidth: 400 } }}
        isLoading={isResetting}
      >
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
          - Password is <strong>temporary</strong>. The user must change their password upon login.
          <br />- Must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, one
          number, and one special character.
        </Typography>
        <Controller
          name="newPassword"
          render={({ field, fieldState }) => (
            <InputV2
              {...field}
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              fullWidth
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          )}
        />

        <Controller
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <InputV2
              {...field}
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              fullWidth
              sx={{ mt: 2 }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          )}
        />
      </DialogModal>
    </FormProvider>
  )
}

export default UserPasswordResetModal
